/**
 * miraTelemetry — collects browser/page telemetry and a ring buffer of
 * errors + failed network requests for the Mira support widget.
 *
 * Installed ONCE per page lifetime (idempotent). All capture is local; nothing
 * leaves the browser until the user clicks "File this report".
 */

const RING_SIZE = 50;
const ring = [];
let installed = false;

function push(entry) {
  ring.push({ ...entry, ts: new Date().toISOString() });
  if (ring.length > RING_SIZE) ring.shift();
}

export function installMiraTelemetry() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // 1) Patch console.error / console.warn
  const origError = console.error;
  const origWarn = console.warn;
  console.error = function (...args) {
    try {
      push({
        kind: "console.error",
        message: args.map((a) => (typeof a === "string" ? a : safeStringify(a))).join(" "),
      });
    } catch (e) { /* swallow */ }
    return origError.apply(this, args);
  };
  console.warn = function (...args) {
    try {
      push({
        kind: "console.warn",
        message: args.map((a) => (typeof a === "string" ? a : safeStringify(a))).join(" "),
      });
    } catch (e) { /* swallow */ }
    return origWarn.apply(this, args);
  };

  // 2) Global error + unhandledrejection
  window.addEventListener("error", (e) => {
    push({
      kind: "window.error",
      message: e.message,
      source: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : null,
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    push({
      kind: "unhandledrejection",
      message: safeStringify(e.reason),
    });
  });

  // 3) Wrap fetch to log 4xx/5xx and network failures
  const origFetch = window.fetch;
  window.fetch = async function (...args) {
    const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "?";
    const method = (args[1] && args[1].method) || "GET";
    const t0 = performance.now();
    try {
      const res = await origFetch.apply(this, args);
      if (res.status >= 400) {
        push({
          kind: "fetch",
          method,
          url,
          status: res.status,
          duration_ms: Math.round(performance.now() - t0),
        });
      }
      return res;
    } catch (err) {
      push({
        kind: "fetch.error",
        method,
        url,
        message: err.message,
        duration_ms: Math.round(performance.now() - t0),
      });
      throw err;
    }
  };
}

function safeStringify(obj) {
  try {
    if (obj instanceof Error) return `${obj.name}: ${obj.message}`;
    return JSON.stringify(obj);
  } catch {
    return String(obj);
  }
}

export function collectTelemetry() {
  if (typeof window === "undefined") return {};
  const nav = window.navigator || {};
  const scr = window.screen || {};
  return {
    url: window.location.href,
    path: window.location.pathname + window.location.search + window.location.hash,
    referrer: document.referrer || null,
    page_title: document.title || null,
    viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 },
    screen: { w: scr.width, h: scr.height, color_depth: scr.colorDepth },
    user_agent: nav.userAgent,
    platform: nav.platform,
    language: nav.language,
    languages: (nav.languages || []).slice(0, 3),
    online: nav.onLine,
    cookies_enabled: nav.cookieEnabled,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timestamp: new Date().toISOString(),
    // App-specific hooks (App.js may set window.__abs_app_state__ for context)
    app_state: window.__abs_app_state__ || null,
  };
}

export function getRecentLogs() {
  // Return a shallow copy so consumers can't mutate the buffer
  return ring.slice();
}

export function clearLogs() {
  ring.length = 0;
}
